// Local helpers for the Scope 2 v2 (categorized) data-entry components.
// The row<->DTO mapping mirrors `mapEmissionsFormDataToElectricityApiData` in
// ../../helpers.js but carries the v2 category fields (category / emissionFactor
// / certificateType / supplierNote for purchased, and the wide on-site
// certificate schema).

import { withDimensionItems } from "../Categories/components/Category/helpers";
import { isFactorRequiredCategory, KWH_MEASURE_CODE } from "./const";

// Shared focus styling: every raw Chakra Input / Select / Textarea on these
// tables uses the lime border on focus rather than Chakra's default blue.
// Spread onto the component, e.g. <Input {...LIME_FOCUS} />.
export const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

/**
 * Resolve the kWh measure id from the org structure. Scope 2 v2 electricity is
 * always measured in kWh, so we find the measure whose `code === "KWH"` across
 * every electricityType's measure list. Mirrors the lookup BespokeFactorModal
 * uses (`electricityMeasures.flatMap(m => m.measures)`).
 */
export const resolveKwhMeasureId = (organizationStructure) => {
  const allMeasures = (organizationStructure?.electricityMeasures || []).flatMap(
    (m) => m.measures || []
  );
  const kwh = allMeasures.find((m) => m.code === KWH_MEASURE_CODE);
  return kwh?.id ?? null;
};

/**
 * Resolve the kWh measure display label (e.g. "kWh") for the read-only Measure
 * column. Falls back to the constant code if no name is present.
 */
export const resolveKwhMeasureLabel = (organizationStructure) => {
  const allMeasures = (organizationStructure?.electricityMeasures || []).flatMap(
    (m) => m.measures || []
  );
  const kwh = allMeasures.find((m) => m.code === KWH_MEASURE_CODE);
  return kwh?.name ?? KWH_MEASURE_CODE;
};

// Format a kWh value (string or number) with thousands separators. Empty
// inputs render as a soft dash so the table column reads cleanly.
export const formatKwh = (value) => {
  if (value === "" || value === null || value === undefined) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString("en-GB", { maximumFractionDigits: 0 });
};

// Coerce a numeric form value to a Number, or null when blank.
const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Map a single purchased-electricity form row to its API DTO. The form row
 * carries flat `dimensionOneItem..Five` fields (wrapped via withDimensionItems)
 * plus the category-specific fields. Fields not relevant to the chosen category
 * are nulled out so stale values never reach the backend.
 */
export const mapPurchasedRowToApiData = (row, kwhMeasureId) => {
  const { dimensionItems } = withDimensionItems({ ...row });
  const category = row.category;
  return {
    id: row.id ?? null,
    dimensionItems,
    consumption: {
      value: toNumberOrNull(row.value),
      measureId: kwhMeasureId,
    },
    category,
    // STANDARD_GRID always falls back to the default factor (null). The three
    // factor-bearing categories keep whatever the user entered.
    emissionFactor:
      category === "STANDARD_GRID" ? null : toNumberOrNull(row.emissionFactor),
    // Renewable type is only meaningful for CERTIFIED_RENEWABLE.
    certificateType:
      category === "CERTIFIED_RENEWABLE" ? row.renewableType || null : null,
    // Supplier/private-wire note only applies to factor-required categories.
    supplierNote: isFactorRequiredCategory(category) ? row.supplierNote || null : null,
  };
};

export const mapPurchasedFormDataToApiData = (rows, kwhMeasureId) =>
  rows.map((row) => mapPurchasedRowToApiData(row, kwhMeasureId));

/**
 * Map a single on-site generation form row to its API DTO. `certificatesIssued`
 * is a real boolean (the form uses "yes"/"no" select values — converted here).
 * When certificates are not issued the dependent certificate fields are nulled.
 */
export const mapOnsiteRowToApiData = (row) => {
  const { dimensionItems } = withDimensionItems({ ...row });
  const issued = row.certificatesIssued === "yes" || row.certificatesIssued === true;
  return {
    id: row.id ?? null,
    dimensionItems,
    fuelType: row.fuelType,
    totalGenerated: toNumberOrNull(row.totalGenerated),
    selfConsumed: toNumberOrNull(row.selfConsumed),
    exportedToGrid: toNumberOrNull(row.exportedToGrid),
    certificatesIssued: issued,
    certificateType: issued ? row.certificateType || null : null,
    certificatesRetained: issued ? toNumberOrNull(row.certificatesRetained) : null,
    certificatesTransferred: issued ? toNumberOrNull(row.certificatesTransferred) : null,
  };
};

export const mapOnsiteFormDataToApiData = (rows) => rows.map(mapOnsiteRowToApiData);
